import { useState } from 'react';
import { HiReceiptRefund } from 'react-icons/hi';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

import { Checkbox, CopyButton } from 'components';
import { integerToCurrency } from 'helpers';
import { Price, ReturnRequestStatus } from 'types';

import { ProductRefundModal } from '../product-refund-modal';
import { ShippingRefundModal } from '../shipping-refund-modal';

interface Product {
  description: string;
  entryNumber: number;
  price: Price;
  sku: string;
  status: string;
}

interface Consignment {
  chargeId: string;
  code: string;
  deliveryCost: {
    price: Price;
    refundedPrice: Price;
  };
  products: Product[];
  recipient: string;
  refundedPrice: Price;
  status: string;
  totalPrice: Price;
}

interface ConsignmentItemProps {
  consignment: Consignment;
}

type Status = 'READY' | 'REFUNDED' | 'CANCELED';

const BORDER_COLOR_BY_STATUS: Record<Status, string> = {
  [ReturnRequestStatus.Ready]: 'border-neutral-500',
  [ReturnRequestStatus.Refunded]: 'border-success-300',
  [ReturnRequestStatus.Canceled]: 'border-neutral-300',
};

export function ConsignmentItem({ consignment }: ConsignmentItemProps) {
  const {
    chargeId,
    code,
    deliveryCost,
    products,
    recipient,
    refundedPrice,
    status,
    totalPrice,
  } = consignment;
  const [isProductsAndShippingExpanded, setIsProductsAndShippingExpanded] =
    useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isShippingRefundModalOpen, setIsShippingRefundModalOpen] =
    useState(false);

  const getProductTextColor = (status: string) =>
    status === ReturnRequestStatus.Refunded ||
    status === ReturnRequestStatus.RefundInProgress
      ? 'text-neutral-300'
      : 'text-neutral-500';

  const getBorderColor = (status?: string) =>
    BORDER_COLOR_BY_STATUS[status?.toUpperCase() as Status] ??
    'border-neutral-500';

  const handleProductSelection = (productId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const selectedProductsData = products.filter((product, index) =>
    selectedProducts.includes(`${product.sku}-${index}`)
  );

  const selectedProductsTotal = selectedProductsData.reduce(
    (total, product) => total + product.price.value,
    0
  );

  const renderRefundStatus = () => {
    const hasProductRefund = refundedPrice.value > 0;
    const hasShippingRefund = deliveryCost.refundedPrice.value > 0;

    if (!hasProductRefund && !hasShippingRefund) return null;

    return (
      <>
        <div className="flex items-center gap-1">
          <HiReceiptRefund className="size-4 text-success-300" />
          <p className="text-font18 font-bold text-neutral-500">Estornado</p>
        </div>
        <div>
          {hasProductRefund && (
            <p className="text-font18 text-neutral-500">
              {integerToCurrency(refundedPrice.value)}
            </p>
          )}
          {hasShippingRefund && (
            <p className="text-font12 text-neutral-500">
              + {integerToCurrency(deliveryCost.refundedPrice.value)}
            </p>
          )}
        </div>
        <p className="text-font10 text-neutral-500">
          (
          {hasProductRefund && hasShippingRefund
            ? 'Produto + frete'
            : hasProductRefund
              ? 'Produto'
              : 'Frete'}
          )
        </p>
      </>
    );
  };

  const renderProductsAndShippingDetails = () => {
    const shouldRenderButton =
      deliveryCost.price.value > 0 &&
      deliveryCost.refundedPrice.value < deliveryCost.price.value;

    return (
      <div className="flex flex-col pb-4">
        <div className="flex flex-col gap-2 p-4 md:gap-6 md:px-8 md:py-4">
          <div className="flex items-center gap-2">
            <p className="text-font14 font-bold text-neutral-700">Produtos</p>
            <div className="h-px flex-grow bg-neutral-300" />
          </div>
          <div className="flex flex-col">
            {products.map((product, index) => (
              <div
                key={`${product.sku}-${index}`}
                className="flex gap-0.5 py-2"
              >
                <div className="flex items-center p-1.5">
                  <Checkbox
                    disabled={
                      product.status === ReturnRequestStatus.Refunded ||
                      product.status === ReturnRequestStatus.RefundInProgress
                    }
                    id={`${product.sku}-${index}`}
                    checked={selectedProducts.includes(
                      `${product.sku}-${index}`
                    )}
                    onChange={e =>
                      handleProductSelection(
                        `${product.sku}-${index}`,
                        e.target.checked
                      )
                    }
                  />
                </div>
                <div
                  className="flex flex-grow flex-col justify-between gap-1 pt-1
                    md:flex-row md:gap-10"
                >
                  <div className="flex w-full flex-col gap-1 xl:min-w-64">
                    <p
                      className={`text-font12 font-bold
                      ${getProductTextColor(product.status)}`}
                    >
                      {product.description}
                    </p>
                    <p
                      className={`text-font12
                      ${getProductTextColor(product.status)}`}
                    >
                      {integerToCurrency(product.price.value)}
                    </p>
                  </div>
                  <div className="w-full xl:min-w-52">
                    <p
                      className={`text-font12
                      ${getProductTextColor(product.status)}`}
                    >
                      {product.sku}
                    </p>
                  </div>
                  <div className="w-full xl:min-w-52">
                    {product.status ===
                      ReturnRequestStatus.RefundInProgress && (
                      <div className="flex items-center gap-0.5">
                        <HiReceiptRefund className="text-primary-300" />
                        <p className="text-font12 text-neutral-500">
                          Produto em devolução
                        </p>
                      </div>
                    )}
                    {product.status === ReturnRequestStatus.Refunded && (
                      <div className="flex items-center gap-0.5">
                        <HiReceiptRefund className="text-success-300" />
                        <p className="text-font12 text-neutral-500">
                          Produto estornado
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {selectedProducts.length > 0 && (
              <div className="flex items-end justify-between">
                <div className="flex gap-1">
                  <p className="text-font14 font-bold text-neutral-700">
                    Seleção de produtos
                  </p>
                  <p className="text-font14 text-neutral-700">
                    {integerToCurrency(selectedProductsTotal)}
                  </p>
                </div>
                <button
                  onClick={() => setIsRefundModalOpen(true)}
                  className="flex w-56 items-center justify-center gap-2
                    rounded-lg bg-primary-300 px-4 py-2 text-font14
                    text-neutral-100"
                >
                  Estornar valor de produtos
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 p-4 md:gap-6 md:px-8 md:py-4">
          <div className="flex items-center gap-2">
            <p className="text-font14 font-bold text-neutral-700">Frete</p>
            <div className="h-px flex-grow bg-neutral-300" />
          </div>
          <div className="flex items-end justify-between">
            <div className="flex flex-col gap-4">
              <div className="flex gap-1">
                <p className="text-font14 font-bold text-neutral-700">
                  Valor original
                </p>
                <p className="text-font14 text-neutral-700">
                  {integerToCurrency(deliveryCost.price.value)}
                </p>
              </div>
              {deliveryCost.refundedPrice.value > 0 && (
                <div className="flex gap-1">
                  <p className="text-font14 font-bold text-neutral-700">
                    Já estornado
                  </p>
                  <p className="text-font14 text-neutral-700">
                    {integerToCurrency(deliveryCost.refundedPrice.value)}
                  </p>
                </div>
              )}
            </div>
            {shouldRenderButton && (
              <button
                onClick={() => setIsShippingRefundModalOpen(true)}
                className="flex w-56 items-center justify-center gap-2
                  rounded-lg bg-neutral-500 px-4 py-2 text-font14
                  text-neutral-100"
              >
                Estornar frete
              </button>
            )}
          </div>
        </div>

        {isRefundModalOpen && (
          <ProductRefundModal
            selectedProducts={selectedProductsData}
            totalValue={selectedProductsTotal}
            consignmentCode={code}
            onClose={() => {
              setIsRefundModalOpen(false);
              setSelectedProducts([]);
            }}
          />
        )}

        {isShippingRefundModalOpen && (
          <ShippingRefundModal
            consignmentCode={code}
            deliveryCost={deliveryCost}
            onClose={() => setIsShippingRefundModalOpen(false)}
          />
        )}
      </div>
    );
  };

  const renderStatus = () => {
    if (status.toUpperCase() !== ReturnRequestStatus.Canceled) return null;

    return (
      <div
        className="relative flex items-center justify-center gap-2.5 rounded-lg
          bg-neutral-500 px-2 py-0.5"
      >
        <p className="text-left text-font12 font-bold text-neutral-100">
          Cancelada
        </p>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col rounded border-l-8 bg-neutral-200 pl-2
        ${getBorderColor(status)} w-full border-solid`}
    >
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-2 text-neutral-500">
          <p className="text-font24 font-bold">{code}</p>
          <CopyButton content={code} />
          {renderStatus()}
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:gap-8">
          <div className="flex w-full flex-grow flex-col gap-2 xl:w-[232px]">
            <div>
              <p className="text-font14 font-bold text-neutral-500">
                Transação
              </p>
              <p className="text-font14 text-neutral-500">ID #{chargeId}</p>
            </div>
            <div>
              <p className="text-font14 font-bold text-neutral-500">
                Recebedor
              </p>
              <p className="text-font14 text-neutral-500">{recipient}</p>
            </div>
          </div>

          <div className="flex w-full flex-grow flex-col gap-2 xl:w-[232px]">
            <div>
              <p className="text-font14 font-bold text-neutral-500">Valor</p>
              <p className="text-font14 text-neutral-500">
                {integerToCurrency(totalPrice.value)}
              </p>
            </div>
            <div>
              <p className="text-font14 font-bold text-neutral-500">Frete</p>
              <p className="text-font14 text-neutral-500">
                {integerToCurrency(deliveryCost.price.value)}
              </p>
            </div>
          </div>

          <div className="flex w-full flex-grow flex-col gap-2 xl:w-[232px]">
            {renderRefundStatus()}
          </div>
        </div>
      </div>

      {isProductsAndShippingExpanded && renderProductsAndShippingDetails()}

      <div className="flex items-center gap-3 p-4">
        <button
          onClick={() => {
            setIsProductsAndShippingExpanded(!isProductsAndShippingExpanded);
            if (isProductsAndShippingExpanded) {
              setSelectedProducts([]);
            }
          }}
          className="flex items-center gap-2 rounded-lg px-0.5 text-font14
            font-bold text-primary-300"
        >
          {isProductsAndShippingExpanded ? 'Recolher' : 'Ver produtos e frete'}
          {isProductsAndShippingExpanded ? (
            <MdKeyboardArrowUp />
          ) : (
            <MdKeyboardArrowDown />
          )}
        </button>
      </div>
    </div>
  );
}
